import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const MaskedLine = ({ children, delay, testid }) => (
  <div className="overflow-hidden">
    <motion.span
      data-testid={testid}
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ delay, duration: 1, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </div>
);

export const Hero = ({ t, scrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = (e, hash) => {
    e.preventDefault();
    scrollTo(hash);
  };

  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src="/images/hero-lake.webp"
          alt="Zugersee mit Alpenpanorama – Swiss PA Service, Relocation Services und Lifestyle Concierge in Zug, Schweiz"
          className="w-full h-full object-cover"
          data-testid="hero-background-image"
        />
        <div className="absolute inset-0 bg-ink/55" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <div className="overflow-hidden mb-6">
          <motion.p
            data-testid="hero-overline"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ delay: 0.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="text-cream/80 text-xs md:text-sm uppercase tracking-[0.25em] font-semibold"
          >
            {t.hero.overline}
          </motion.p>
        </div>

        <h1 className="font-heading font-light text-cream leading-[0.95] tracking-tight text-6xl md:text-8xl lg:text-9xl">
          <MaskedLine delay={0.25} testid="hero-title-line-1">{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.4} testid="hero-title-line-2">
            <span className="italic text-cream/90">{t.hero.line2}</span>
          </MaskedLine>
        </h1>

        <div className="mt-8 max-w-xl">
          <MaskedLine delay={0.6} testid="hero-subtitle">
            <span className="font-heading text-2xl md:text-3xl text-cream/95 font-normal">{t.hero.sub}</span>
          </MaskedLine>
          <MaskedLine delay={0.72} testid="hero-description">
            <span className="mt-3 block text-cream/75 text-sm md:text-base font-light leading-relaxed">{t.hero.desc}</span>
          </MaskedLine>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#kontakt"
            data-testid="hero-cta-primary"
            onClick={(e) => go(e, "#kontakt")}
            className="inline-flex items-center gap-2 bg-cream text-ink px-7 py-4 text-sm font-semibold tracking-wide hover:bg-taupe hover:text-cream transition-colors duration-300"
          >
            {t.hero.ctaPrimary}
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
          <a
            href="#leistungen"
            data-testid="hero-cta-secondary"
            onClick={(e) => go(e, "#leistungen")}
            className="inline-flex items-center gap-2 border border-cream/40 text-cream px-7 py-4 text-sm tracking-wide hover:bg-cream/10 transition-colors duration-300"
          >
            {t.hero.ctaSecondary}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 hidden md:flex flex-col items-center gap-3 text-cream/70"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] rotate-90 origin-center translate-y-[-8px]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={16} strokeWidth={1.5} />
        </motion.span>
      </motion.div>
    </section>
  );
};
